/**
 * CUSTOM: harempants — Custom JavaScript
 *
 * This file contains custom fixes and enhancements for harempants.com.
 * Loaded via layout/theme.liquid before </body>.
 */

/* ==========================================================================
   Feature: Collapsible "Read more" for collection banner descriptions

   Long collection descriptions push the product grid below the fold. This
   collapses each .collection-hero__description to --crm-lines (3) with a fade
   and a "View more"/"View less" toggle.

   Pairs with the .crm--* / .collection-hero__description rules in custom.css,
   which provide the collapsed clamp + fade styling. This script, per
   description:
     - if the text overflows the line limit  → add .crm--collapsed + a toggle
     - if it fits                            → leave it alone (no toggle/clamp)
   Re-evaluates on resize (text re-wraps at different widths) but never
   re-collapses something the visitor chose to expand. Re-runs on
   shopify:section:load so it works in the theme editor.

   The section wraps the description in a <motion-element> whose fade-up reveal
   starts at translateY(25px). That transform inflates the wrapper's
   scrollHeight, so overflow is measured from the inner element's offsetHeight
   (transform-immune) rather than the wrapper's scroll/clientHeight. Kept first
   in this file so an error in an unrelated IIFE below can't stop it running.
   ========================================================================== */
(function () {
  'use strict';

  var SHOW_TEXT = 'View more';
  var HIDE_TEXT = 'View less';

  function lineHeight(el) {
    return parseFloat(getComputedStyle(el).lineHeight) || 24;
  }

  /* Does the content exceed the line limit? Measure the inner element's
     offsetHeight (layout height, immune to the motion-element's translateY
     transform) rather than the wrapper's scrollHeight, which the transform
     would inflate and cause false positives on short descriptions. */
  function overflows(desc) {
    var lh = lineHeight(desc);
    var lines = parseInt(getComputedStyle(desc).getPropertyValue('--crm-lines'), 10) || 3;
    var content = desc.firstElementChild || desc;
    return content.offsetHeight > lh * lines + Math.max(4, lh * 0.4);
  }

  function makeToggle(desc) {
    var wrap = document.createElement('div');
    wrap.className = 'crm__toggle-wrap';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn--underline crm__toggle';
    btn.setAttribute('aria-expanded', 'false');
    if (desc.id) btn.setAttribute('aria-controls', desc.id);
    btn.innerHTML = '<span class="btn__text">' + SHOW_TEXT + '</span>';
    btn.addEventListener('click', function () {
      var expanded = desc.classList.toggle('crm--expanded');
      desc.classList.toggle('crm--collapsed', !expanded);
      btn.setAttribute('aria-expanded', String(expanded));
      btn.querySelector('.btn__text').textContent = expanded ? HIDE_TEXT : SHOW_TEXT;
    });
    wrap.appendChild(btn);
    return wrap;
  }

  function setup(desc) {
    if (!desc) return;
    if (!desc.id) desc.id = 'collection-desc-' + Math.random().toString(36).slice(2, 8);

    var existing = desc.parentElement.querySelector(':scope > .crm__toggle-wrap');
    var wasExpanded = desc.classList.contains('crm--expanded');

    /* Reset to a clean, measured state. */
    desc.classList.remove('crm--ready', 'crm--expanded', 'crm--collapsed');
    var isOverflowing = overflows(desc);
    desc.classList.add('crm--ready');

    if (!isOverflowing) {
      if (existing) existing.remove();
      return;
    }

    var wrap = existing || makeToggle(desc);
    if (!existing) desc.insertAdjacentElement('afterend', wrap);
    var btn = wrap.querySelector('.crm__toggle');

    if (wasExpanded) {
      desc.classList.add('crm--expanded');
      btn.setAttribute('aria-expanded', 'true');
      btn.querySelector('.btn__text').textContent = HIDE_TEXT;
    } else {
      desc.classList.add('crm--collapsed');
      btn.setAttribute('aria-expanded', 'false');
      btn.querySelector('.btn__text').textContent = SHOW_TEXT;
    }
  }

  function setupAll() {
    document.querySelectorAll('.collection-hero__description').forEach(setup);
  }

  function init() {
    if (!document.querySelector('.collection-hero__description')) return;
    setupAll();

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(setupAll, 200);
    });

    document.addEventListener('shopify:section:load', setupAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================================================
   Fix: a Variant King combined-listing card must never describe one colourway
        while showing another, on collection pages
   Tests: tests/bugs/variant-king-hover-coherence.spec.ts
          tests/bugs/variant-king-atc-button.spec.ts
   State doc: plans/bugs/bug-variant-king-hover-coherence.md
              plans/bugs/bug-variant-king-atc-button.md

   The Variant King "Combined Listings" app merges separate per-colour PRODUCTS
   into one collection card with colour swatches. Committing a swatch rewrites the
   card's title, image, URL and hidden variant id — but NOT:
     - the Add-to-Cart button's disabled state or text
     - the `.product-card__badge > .f-badge--soldout` span's visibility
   And on HOVER the app previews the PHOTOGRAPH only, from its own
   `.vkcl-product-images-data` table, leaving title, URL and availability on the
   committed colourway. A card therefore showed an in-stock colourway's photograph
   under a "Sold out" badge and a disabled button for as long as a pointer rested
   on the swatch. This file closes both halves of that.

   TWO LEVELS OF SELECTION
     COMMITTED — `li.swatch-view-item[aria-checked="true"]` inside the card's
                 `<variant-king-combined-listing>`. The colourway the shopper
                 actually chose. Variant King owns it; this file never writes it.
                 `.swatch-selected` is a fallback used only when `aria-checked` is
                 absent entirely (mid-render).
     PREVIEWED — the swatch under the mouse or holding keyboard focus. Transient,
                 layered on top, dropped the instant pointer or focus leaves.
   A colourway is sold out iff its `li` carries `swatch-item-unavailable`. Both
   facts are read off the live DOM, never assumed.

   WHAT A PREVIEW SWAPS
   Everything keyed off availability — badge, button, quick-add — plus the photo
   and the identity (title text and the two links that promise it), so the card
   never names one colourway while picturing or linking to another. Price is
   deliberately NOT previewed: committing does not change it either, because every
   colourway of a combined listing shares one price block, so previewing one would
   invent data.

   A PREVIEW IS A LOOK, NEVER AN AFFORDANCE
   While previewing, `.product-card__actions` is `inert`. The form's hidden variant
   id belongs to the COMMITTED colourway, so a previewed quick-add must be visible
   and unusable. This is not a detail: an earlier hover-aware patch preferred the
   event target over `aria-checked` and shipped a working "Add to cart" over a
   sold-out variant. `inert` is what separates this from that.

   IDENTITY IS NEVER INVENTED
   A swatch's `orig-value` is the option value the MERCHANT configured for that
   Variant King group — on some groups a full product title, on others a bare
   colour ("White"). Nothing on the card marks which. So the vocabulary is PROVEN
   per card: the committed colourway's title is known for certain (Liquid rendered
   it, or Variant King wrote it on commit), so if the committed swatch's LABEL
   equals that title, this group labels swatches with titles and a sibling's label
   is its title too. When the proof fails, the title and links stay committed —
   which is exactly what Variant King itself does. Title and links move together
   or not at all.

   TOUCH
   Pointer preview is gated on `pointerType === 'mouse'`. A finger cannot stop
   hovering, so touch goes straight to the commit path and no tap can strand a card
   in a preview it can never leave. Keyboard focus previews on every device.

   NO TIMERS
   Variant King's DOM work lands whenever it lands — throttled CPU, cold cache, a
   grid re-render from facets or load-more — so sampling the DOM after a fixed
   delay is a race that fails silently. MutationObservers watch the swatch list,
   pointer/focus events record what is previewed, and the card is recomputed from
   scratch on every change. Renders coalesce to the next frame, which is also the
   only "debounce" a fast swipe across the swatch row gets — frame-based, so it
   cannot become a race.

   SCOPE
   Only cards containing a `<variant-king-combined-listing>` are ever visited, so
   ordinary cards are untouched. Cards belonging to a sold-out design candidate
   (`data-soldout-v*` on the card root) are skipped — those ship their own state
   script and own their own affordances.

   IDEMPOTENT
   Every write is guarded by a read, so a recompute caused by our own mutation
   produces no further mutation and the observers settle after one extra pass. A
   card that is never previewed is never written to beyond its affordances, so its
   Liquid-rendered responsive image and title survive untouched.
   ========================================================================== */
(function () {
  'use strict';

  var SWATCH_SELECTOR = 'li.swatch-view-item';
  var TITLE_SEL = '.product-card__title a';
  var MEDIA_LINK_SEL = '.product-card__image-wrapper > a[href]';
  var IMG_SEL = '.product-card__image--main img';
  var PREVIEW_ATTR = 'data-vk-previewing';

  /* Labels are learned from the page rather than hardcoded: Liquid renders them
     through the theme's locale files, so a translated storefront must not be handed
     English. These constants are only the fallback for a grid that shows neither
     state. */
  var SOLD_OUT_TEXT = 'Sold out';
  var ADD_TO_CART_TEXT = 'Add to cart';
  var labelsLearned = false;

  var wired = new WeakSet();
  var queue = [];
  var applyQueued = false;
  var scanQueued = false;

  /* Pointer and focus are tracked separately so moving the mouse away cannot silently
     cancel a preview the keyboard is still holding, and vice versa. */
  var pointerPreview = new WeakMap();
  var focusPreview = new WeakMap();
  /* Cards that have previewed some OTHER colourway. Until that happens this file never
     touches the photograph, so an untouched card keeps exactly the responsive markup
     Liquid rendered. */
  var contentOwned = new WeakSet();
  /* Snapshots of someone else's truth, taken before any preview: Liquid's at render,
     Variant King's after a commit. */
  var imageOrigin = new WeakMap();
  var titleOrigin = new WeakMap();
  /* Cards showing a previewed identity -> the exact title text this file wrote there.
     Kept as the text, not a flag, so a restore can check the title is still the one it
     put there: a click ends the preview in the same tick Variant King rewrites the
     title, and whichever lands second must not clobber the other. */
  var titleWritten = new WeakMap();

  /* --- scheduling ------------------------------------------------------- */
  /* rAF coalesces the burst of mutations Variant King emits in one tick. The
     setTimeout is a co-runner, not a fallback delay: whichever fires first wins
     and the other becomes a no-op, so a throttled or backgrounded rAF cannot
     strand a card in a stale state. */
  function soon(fn) {
    var done = false;
    function run() {
      if (done) return;
      done = true;
      fn();
    }
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
    setTimeout(run, 0);
  }

  function scheduleApply(card) {
    if (queue.indexOf(card) === -1) queue.push(card);
    if (applyQueued) return;
    applyQueued = true;
    soon(function () {
      applyQueued = false;
      var batch = queue;
      queue = [];
      for (var i = 0; i < batch.length; i++) applyState(batch[i]);
    });
  }

  function scheduleScan() {
    if (scanQueued) return;
    scanQueued = true;
    soon(function () {
      scanQueued = false;
      scan();
    });
  }

  /* --- state ------------------------------------------------------------ */

  /* A card rendered by one of the sold-out design candidates carries its own
     runtime script; this patch must not fight it for the same button. */
  function isDesignOwned(card) {
    var attrs = card.attributes;
    for (var i = 0; i < attrs.length; i++) {
      if (attrs[i].name.indexOf('data-soldout-') === 0) return true;
    }
    return false;
  }

  /* One pass over the grid: the sold-out label is whatever Liquid put in a sold-out
     badge, the add-to-cart label whatever it put in an enabled card's button. Stops
     looking once both are found. */
  function learnLabels() {
    if (labelsLearned) return;
    var badge = document.querySelector('.product-card__badge .f-badge--soldout');
    var badgeText = badge && badge.textContent ? badge.textContent.trim() : '';
    if (badgeText) SOLD_OUT_TEXT = badgeText;

    var buttons = document.querySelectorAll('button[name="add"].product-card__atc');
    var atcText = '';
    for (var i = 0; i < buttons.length && !atcText; i++) {
      if (buttons[i].disabled) continue;
      var t = buttons[i].querySelector('.product-card__atc-text');
      if (t && t.textContent.trim()) atcText = t.textContent.trim();
    }
    if (atcText) ADD_TO_CART_TEXT = atcText;
    if (badgeText && atcText) labelsLearned = true;
  }

  /* The COMMITTED colourway, or null when this card is not a combined listing. Null is
     the "leave Liquid's decision alone" signal — a card without Variant King must be
     completely unaffected by this file. */
  function activeSwatch(card) {
    var vk = card.querySelector('variant-king-combined-listing');
    if (!vk) return null;
    var checked = vk.querySelector(SWATCH_SELECTOR + '[aria-checked="true"]');
    if (checked) return checked;
    /* Variant King's own "selected" marker, used only if aria-checked is missing
       entirely (e.g. mid-render). Still a selection, never a preview. */
    var marked = vk.querySelector('.swatch-selected');
    return marked && marked.closest ? marked.closest(SWATCH_SELECTOR) : null;
  }

  /* Product title of a colourway. `orig-value` is the raw label; `aria-label` is the
     same string with " - Sold Out" appended when unavailable, so it is the fallback,
     not the source. */
  function swatchTitle(swatch) {
    var raw = swatch.getAttribute('orig-value');
    if (raw) return raw;
    var label = swatch.getAttribute('aria-label') || '';
    return label.replace(/\s+-\s+sold\s*out\s*$/i, '');
  }

  /* Product URL of a colourway. Lives on the inner `.swatch-image` element, which is
     also the key into the images table below. */
  function swatchUrl(swatch) {
    var el = swatch.querySelector('[swatch-url]');
    return el ? el.getAttribute('swatch-url') : null;
  }

  /* Variant King ships a hidden per-colourway image table inside every combined
     listing; it is what the app's own hover swap reads. Reusing it keeps the preview
     and the commit on ONE source of truth instead of two that can disagree. */
  function featuredImageFor(card, url) {
    if (!url) return null;
    var vk = card.querySelector('variant-king-combined-listing');
    if (!vk) return null;
    var rows = vk.querySelectorAll('.vkcl-product-images-data [data-product-handle]');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute('data-product-handle') === url) {
        return rows[i].getAttribute('data-featured-image') || null;
      }
    }
    return null;
  }

  /* --- the responsive image Liquid rendered ------------------------------ */

  /* The attributes that MAKE an image responsive. `srcset` + `sizes` are the pair the
     browser picks a candidate from, `width`/`height` carry the intrinsic ratio, and
     `loading` decides whether it is fetched at all. Variant King's table holds one raw,
     full-size CDN URL per colourway and no `srcset` whatsoever, so writing an image
     from it destroys every one of them — and nothing can reconstruct them afterwards.
     Order matters: `sizes` and `srcset` land before `src`, so candidate selection never
     runs against a bare full-size `src`. */
  var IMAGE_ATTRS = ['sizes', 'srcset', 'data-srcset', 'src', 'width', 'height', 'loading', 'alt'];

  /* One snapshot per <img>, taken before anything has written to it. Keyed on the
     element, so a grid re-render (facets, sorting, load-more) arrives with a fresh
     <img> and takes a fresh snapshot instead of inheriting a stale one. */
  function photoId(url) {
    var path = String(url || '').split('?')[0];
    return path.substring(path.lastIndexOf('/') + 1) || null;
  }

  function rememberImage(card) {
    var img = card.querySelector(IMG_SEL);
    if (!img || imageOrigin.has(img)) return;
    var attrs = {};
    for (var i = 0; i < IMAGE_ATTRS.length; i++) attrs[IMAGE_ATTRS[i]] = img.getAttribute(IMAGE_ATTRS[i]);
    imageOrigin.set(img, { photo: photoId(attrs.src), attrs: attrs });
  }

  /* Put Liquid's markup back attribute for attribute. The browser re-runs candidate
     selection, lands on the URL it already has decoded, and paints it with no refetch
     and no un-painted frame. Writing the raw CDN URL here instead — which is what
     Variant King's own hover revert does — costs a full-size download on every
     hover-out and blanks the card while it arrives. */
  function restoreImage(img, attrs) {
    for (var i = 0; i < IMAGE_ATTRS.length; i++) {
      var name = IMAGE_ATTRS[i];
      var want = attrs[name];
      if (want === null) {
        if (img.hasAttribute(name)) img.removeAttribute(name);
      } else if (img.getAttribute(name) !== want) {
        img.setAttribute(name, want);
      }
    }
  }

  function sizedUrl(raw, w) {
    return raw + (raw.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  /* Shopify's image CDN resizes any of its own URLs on demand through `&width=`, which
     holds for the raw URLs in Variant King's table too. So the width ladder Liquid's
     own `srcset` already asked for is re-applied to the previewed colourway, and the
     browser keeps choosing against the unchanged `sizes` rule. With no ladder to copy,
     the raw URL is written unchanged — the behaviour that preceded this, never worse. */
  function previewImage(img, raw, origin) {
    var parts = String((origin && origin.attrs.srcset) || '').split(',');
    var widths = [];
    for (var i = 0; i < parts.length; i++) {
      var m = /(\d+)w$/.exec(parts[i].trim());
      if (m) widths.push(m[1]);
    }
    var srcset = raw;
    var src = raw;
    if (widths.length) {
      var candidates = [];
      for (var j = 0; j < widths.length; j++) {
        candidates.push(sizedUrl(raw, widths[j]) + ' ' + widths[j] + 'w');
      }
      srcset = candidates.join(', ');
      src = sizedUrl(raw, widths[widths.length - 1]);
    }
    if (img.getAttribute('srcset') !== srcset) img.setAttribute('srcset', srcset);
    if (img.hasAttribute('data-srcset') && img.getAttribute('data-srcset') !== srcset) {
      img.setAttribute('data-srcset', srcset);
    }
    if (img.getAttribute('src') !== src) img.setAttribute('src', src);
  }

  /* The photograph of one colourway. Runs in BOTH directions — previewing a colourway
     and reverting to the committed one go through the identical code — so a revert
     cannot drift from what was rendered before it. */
  function syncPhoto(card, swatch) {
    var url = swatchUrl(swatch);
    var img = card.querySelector(IMG_SEL);
    var raw = featuredImageFor(card, url);
    if (!img || !raw) return;
    var origin = imageOrigin.get(img);
    if (origin && photoId(raw) === origin.photo) {
      /* The colourway on show is the one Liquid rendered, so its photograph is already
         in this element, already decoded, in markup nothing else can reproduce. Put
         that markup back verbatim rather than re-deriving it. */
      restoreImage(img, origin.attrs);
    } else {
      /* Mirrors what Variant King's own swap writes — the same photograph, sized for
         the slot — so when both run the second is a no-op instead of a fight. */
      previewImage(img, raw, origin);
      var title = knownTitleFor(card, swatch);
      if (title && img.getAttribute('alt') !== title) img.setAttribute('alt', title);
    }
  }

  /* --- identity: the title and the links that promise it ----------------- */

  function titleEl(card) {
    return card.querySelector(TITLE_SEL) || card.querySelector('.product-card__title');
  }

  function readTitleText(el) {
    var carrier = el.querySelector('.reversed-link__text');
    return carrier ? carrier.textContent : el.textContent;
  }

  /* Compare the way an accessible-name computation would, so Liquid's indentation and
     Variant King's own whitespace cannot make two identical titles look different. */
  function sameText(a, b) {
    return String(a == null ? '' : a).replace(/\s+/g, ' ').trim() ===
      String(b == null ? '' : b).replace(/\s+/g, ' ').trim();
  }

  function setHref(el, url) {
    if (el && url && el.getAttribute('href') !== url) el.setAttribute('href', url);
  }

  /* The theme wraps a reversed-link's text in `.reversed-link__text` (its underline
     animation keys off that element). Variant King flattens the link to a bare, indented
     text node on commit, so the carrier is re-established rather than assumed, and loose
     text nodes are dropped on every pass. */
  function setTitleText(link, text) {
    for (var i = link.childNodes.length - 1; i >= 0; i--) {
      if (link.childNodes[i].nodeType === 3) link.removeChild(link.childNodes[i]);
    }
    var span = link.querySelector('.reversed-link__text');
    if (!span) {
      span = document.createElement('span');
      span.className = 'reversed-link__text';
      link.insertBefore(span, link.firstChild);
    }
    if (span.textContent !== text) span.textContent = text;
  }

  /* --- the vendor's own storefront cache --------------------------------- */

  /* Variant King stores one JSON record per product it knows about in sessionStorage —
     `{"handle":"…-in-white","title":"Solid Color Drawstring Women's Yoga Massage Pants
     in White",…}` — under keys beginning `starapps_vkcl_storefront_`, and writes its own
     card titles out of it. Indexed by handle it is the one place on the page where a
     colourway's real title is stated for a group whose swatches are labelled with bare
     colours ("White", "Black") rather than titles.

     It is a VENDOR-PRIVATE key: the prefix, the key suffix and the value's shape are the
     app's business and can change without notice. So this is an optimisation with a
     fallback, not an interface — every path answers null instead of throwing, and null
     puts the caller back on the Liquid-anchored proof below. */
  var VK_STORE_PREFIX = 'starapps_vkcl_storefront_';

  /* Safari in private mode throws on the property itself, not merely on the read. */
  function sessionStore() {
    try {
      return window.sessionStorage || null;
    } catch (e) {
      return null;
    }
  }

  function storeLength() {
    var store = sessionStore();
    try {
      return store ? store.length : 0;
    } catch (e) {
      return 0;
    }
  }

  function handleOf(url) {
    var p = String(url || '').split('?')[0].replace(/\/+$/, '');
    var i = p.lastIndexOf('/');
    return (i > -1 ? p.substring(i + 1) : p) || null;
  }

  var storefrontTitles = null; /* handle → title; null until the first read */
  var storefrontKeys = 0; /* how many keys the store held at that read */

  function readStorefront() {
    var store = sessionStore();
    var n = storeLength();
    var map = Object.create(null);
    storefrontKeys = n;
    storefrontTitles = map;
    if (!store) return;
    for (var i = 0; i < n; i++) {
      var raw;
      try {
        var key = store.key(i);
        if (!key || key.indexOf(VK_STORE_PREFIX) !== 0) continue;
        raw = store.getItem(key);
      } catch (e) {
        continue;
      }
      if (!raw) continue;
      var value;
      try {
        value = JSON.parse(raw);
      } catch (e) {
        continue; /* half-written, or the key re-purposed — not an answer, not an error */
      }
      if (!value || typeof value !== 'object') continue;
      if (
        typeof value.handle === 'string' && value.handle &&
        typeof value.title === 'string' && value.title
      ) {
        map[value.handle] = value.title;
      }
    }
  }

  /* The real product title of a colourway, or null. The app fills the store
     asynchronously, so a handle absent now is routinely present a moment later and a
     remembered "no" would outlive the fact: a miss re-reads whenever the store has grown
     since the last read, and only then, so a handle the app never caches costs a length
     comparison rather than a full re-parse on every recompute. */
  function storefrontTitle(url) {
    var handle = handleOf(url);
    if (!handle) return null;
    if (storefrontTitles === null || (!storefrontTitles[handle] && storeLength() !== storefrontKeys)) {
      readStorefront();
    }
    return storefrontTitles[handle] || null;
  }

  /* The real product title of `swatch`, or null when it cannot be known from the page.

     The app's cache is asked first — it answers for colour-labelled groups, which the
     proof below can never resolve. Failing that, the committed swatch is the control:
     its product title is known for certain (it is the text standing in the card, put
     there by Liquid or by Variant King's commit), so comparing its LABEL against it
     settles what the whole group's labels are. A match licenses previewing a sibling's
     label as its title; a mismatch is the only honest reason to leave the title alone. */
  function knownTitleFor(card, swatch) {
    var cached = storefrontTitle(swatchUrl(swatch));
    if (cached) return cached;
    var snap = titleOrigin.get(card);
    var committed = activeSwatch(card);
    if (!snap || !committed) return null;
    if (!sameText(swatchTitle(committed), snap.text)) return null;
    return swatchTitle(swatch) || null;
  }

  /* The vendor media link carries the product title as its accessible name, so the label
     has to move with the href — otherwise the card offers a link announced as one
     colourway that navigates to another. */
  function setMediaLink(el, url, label) {
    if (!el) return;
    setHref(el, url);
    if (label != null && el.hasAttribute('aria-label') && el.getAttribute('aria-label') !== label) {
      el.setAttribute('aria-label', label);
    }
  }

  /* Identity of the card: title text plus the two links that promise it. `preview` is the
     previewed swatch, or null when nothing is previewed. Title and links move together or
     not at all — a card that names one colourway while linking to another is worse than
     one that previews neither. */
  function syncIdentity(card, preview) {
    var el = titleEl(card);
    if (!el) return;
    var link = card.querySelector(TITLE_SEL);
    var mediaLink = card.querySelector(MEDIA_LINK_SEL);

    if (!preview) {
      var snap = titleOrigin.get(card);
      var mine = titleWritten.get(card);
      if (mine != null) {
        titleWritten.delete(card);
        /* Restore only what is still ours. A commit ends the preview AND rewrites the
           title; if Variant King's write landed first, that title is the newer truth. */
        if (snap && sameText(readTitleText(el), mine)) {
          setTitleText(el, snap.text);
          setHref(link, snap.href);
          setMediaLink(mediaLink, snap.mediaHref, snap.mediaLabel);
        }
      }
      /* Whatever stands now is the committed truth, by definition — nothing in this file
         has written to it. Re-read on every pass so the title Variant King writes on
         commit becomes the thing a later preview reverts to. An empty read is a
         mid-rewrite frame, not a title, and must never become the snapshot. */
      var text = readTitleText(el);
      if (text && text.trim()) {
        /* Variant King rewrites the title and both hrefs on COMMIT but leaves the media
           link's `aria-label` on the previous colourway, so the card's image link
           announces a product it no longer points at. Measured on live: committing
           "…in White" left the link named "…in Black". The accessible name of that link
           is the card title, always — corrected before it is snapshotted, so a later
           preview reverts to the right one. */
        if (
          mediaLink &&
          mediaLink.hasAttribute('aria-label') &&
          !sameText(mediaLink.getAttribute('aria-label'), text)
        ) {
          mediaLink.setAttribute('aria-label', text.replace(/\s+/g, ' ').trim());
        }
        titleOrigin.set(card, {
          text: text,
          href: link ? link.getAttribute('href') : null,
          mediaHref: mediaLink ? mediaLink.getAttribute('href') : null,
          mediaLabel: mediaLink ? mediaLink.getAttribute('aria-label') : null,
        });
      }
      return;
    }

    var title = knownTitleFor(card, preview);
    if (!title) return;
    var url = swatchUrl(preview);
    titleWritten.set(card, title);
    setTitleText(el, title);
    setHref(link, url);
    setMediaLink(mediaLink, url, title);
  }

  function updateATCButton(card, isSoldOut, previewing) {
    /* `inert` is what makes a preview a LOOK and not an affordance: the form's hidden
       variant id still belongs to the COMMITTED colourway, so a previewed quick-add
       must be visible and unusable. Without it, previewing an in-stock colourway on a
       sold-out card IS the regression this file's predecessor shipped. */
    var actions = card.querySelectorAll('.product-card__actions');
    for (var a = 0; a < actions.length; a++) {
      if ('inert' in actions[a] && actions[a].inert !== previewing) actions[a].inert = previewing;
    }

    var btn = card.querySelector('button[name="add"].product-card__atc');
    if (!btn) return;

    var textEl = btn.querySelector('.product-card__atc-text');
    var formInput = card.querySelector('input.product-variant-id, input[name="id"]');
    var label = isSoldOut ? SOLD_OUT_TEXT : ADD_TO_CART_TEXT;

    if (btn.disabled !== isSoldOut) btn.disabled = isSoldOut;
    if (formInput && formInput.disabled !== isSoldOut) formInput.disabled = isSoldOut;
    if (textEl && textEl.textContent !== label) textEl.textContent = label;
  }

  /* Toggle the sold-out badge inside .product-card__badge. The badge container
     can also hold other badges (sale, new, hot, coming) so we only touch the
     .f-badge--soldout span, not the whole container. */
  function updateSoldOutBadge(card, isSoldOut) {
    var soldOutSpan = card.querySelector('.product-card__badge .f-badge--soldout');
    if (!soldOutSpan) {
      /* Liquid renders this badge — and its container — only for a product that was
         sold out AT RENDER TIME (`pcard-badges.liquid`). On a card committed to an
         in-stock colourway there is therefore nothing to unhide when a sold-out sibling
         is shown, and the card would carry a disabled "Sold out" button with no badge
         above it. So it is created on demand, matching the vendor's markup —
         `aria-hidden`, because the status is already carried by the button's own text.
         `.product-card__badge` is absolutely positioned, so adding one costs no layout.
         Created only when something is actually sold out; an in-stock card is never
         given an empty container. */
      if (!isSoldOut) return;
      var wrapper = card.querySelector('.product-card__image-wrapper');
      if (!wrapper) return;
      var box = card.querySelector('.product-card__badge');
      if (!box) {
        box = document.createElement('div');
        box.className = 'product-card__badge';
        wrapper.appendChild(box);
      }
      soldOutSpan = document.createElement('span');
      soldOutSpan.className = 'f-badge f-badge--soldout';
      soldOutSpan.setAttribute('aria-hidden', 'true');
      soldOutSpan.textContent = SOLD_OUT_TEXT;
      /* First in the container, which is where Liquid puts it among sale/tag badges. */
      box.insertBefore(soldOutSpan, box.firstChild);
    }
    var display = isSoldOut ? '' : 'none';
    if (soldOutSpan.style.display !== display) soldOutSpan.style.display = display;
  }

  /* The previewed swatch, or null. Pointer wins over focus: it is the more recent
     physical intent, and the one that can be withdrawn without a keystroke. */
  function previewSwatch(card) {
    return pointerPreview.get(card) || focusPreview.get(card) || null;
  }

  function applyState(card) {
    var committed = activeSwatch(card);
    if (!committed) return; /* no Variant King → Liquid's decision stands, untouched */

    var p = previewSwatch(card);
    /* A preview OF the committed colourway is not a preview — it shows exactly what is
       already committed, so it must not mark the card transient or inert the quick-add. */
    var previewing = !!p && p !== committed && card.contains(p);
    var shown = previewing ? p : committed;
    var isSoldOut = shown.classList.contains('swatch-item-unavailable');

    if (card.hasAttribute(PREVIEW_ATTR) !== previewing) {
      if (previewing) card.setAttribute(PREVIEW_ATTR, '');
      else card.removeAttribute(PREVIEW_ATTR);
    }

    /* Identity first: it can rewrite the title link's children. Unconditional, because
       the snapshot it keeps has to be taken before a card is ever previewed — a card
       that is never previewed is only ever read, never written. */
    syncIdentity(card, previewing ? shown : null);
    updateATCButton(card, isSoldOut, previewing);
    updateSoldOutBadge(card, isSoldOut);
    if (contentOwned.has(card)) syncPhoto(card, shown);
  }

  function setPreview(map, card, swatch) {
    var current = map.get(card) || null;
    if (current === (swatch || null)) return;
    if (swatch) map.set(card, swatch);
    else map.delete(card);
    /* Only a preview of a DIFFERENT colourway makes this file the owner of the card's
       photograph. Hovering the already-selected swatch leaves Liquid's responsive image
       alone. */
    if (swatch && swatch !== activeSwatch(card)) contentOwned.add(card);
    scheduleApply(card);
  }

  /* --- wiring ----------------------------------------------------------- */

  function swatchFrom(vk, node) {
    if (!node || !node.closest) return null;
    var li = node.closest(SWATCH_SELECTOR);
    return li && vk.contains(li) ? li : null;
  }

  function wire(vk) {
    var card = vk.closest ? vk.closest('.product-card') : null;
    if (!card || isDesignOwned(card)) return;

    if (!wired.has(vk)) {
      wired.add(vk);
      new MutationObserver(function () {
        scheduleApply(card);
      }).observe(vk, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-checked', 'class'],
      });

      /* Pointer preview. `pointerover` bubbles, so one delegated listener covers every
         swatch including ones added by a later re-render. Anything that is not a mouse
         — finger, pen — never previews: it has no way to stop hovering. */
      vk.addEventListener('pointerover', function (e) {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        setPreview(pointerPreview, card, swatchFrom(vk, e.target));
      });
      /* Leaving the swatch group entirely. `pointerover` above already handles moving
         off a swatch into the gap between them. */
      vk.addEventListener('pointerleave', function () {
        setPreview(pointerPreview, card, null);
      });
      vk.addEventListener('pointercancel', function () {
        setPreview(pointerPreview, card, null);
      });

      /* Keyboard parity. Focus previews exactly like hover; blur reverts. Nothing here
         moves focus, so there is no trap — Tab always leaves. */
      vk.addEventListener('focusin', function (e) {
        setPreview(focusPreview, card, swatchFrom(vk, e.target));
      });
      vk.addEventListener('focusout', function (e) {
        /* Focus moving between swatches: focusout fires before the next focusin, whose
           relatedTarget is still inside the group, so the preview must survive it. */
        if (!e.relatedTarget || !vk.contains(e.relatedTarget)) {
          setPreview(focusPreview, card, null);
        }
      });
    }
    scheduleApply(card);
  }

  function scan() {
    learnLabels();
    var listings = document.querySelectorAll('variant-king-combined-listing');
    for (var i = 0; i < listings.length; i++) {
      var card = listings[i].closest ? listings[i].closest('.product-card') : null;
      /* Snapshot before wiring: the responsive markup has to be captured while it is
         still Liquid's, and both this file and Variant King write over it later. */
      if (card && !isDesignOwned(card)) rememberImage(card);
      wire(listings[i]);
    }
  }

  /* Variant King mounts asynchronously and the grid is replaced wholesale by
     facets, sorting and load-more. A coalesced childList observer on the document
     is the one hook that survives all of that; wire() is cheap and
     self-deduplicating, so a re-scan costs a querySelectorAll and nothing else. */
  new MutationObserver(scheduleScan).observe(document.documentElement, {
    subtree: true,
    childList: true,
  });

  scan();
  document.addEventListener('DOMContentLoaded', scan);
  document.addEventListener('shopify:section:load', scan);
})();

/* ==========================================================================
   Feature: Back In Stock notifications on product pages
   Test: tests/bugs/back-in-stock-product-page.spec.ts
   Plan: plans/plan-back-in-stock-product-page.md

   On /products/* pages, any sold-out `.product-form__submit` button is
   rewired into a Back In Stock (Amp) trigger:
     - button text swapped to "Notify me when available"
     - `disabled` removed, `data-bis-proxy="true"` added
     - click is intercepted and forwarded to a hidden `#BIS_trigger` anchor
       that the BIS app binds to

   A single hidden `#BIS_trigger` is appended once. Its `data-variant-id` is
   kept in sync via the theme's pub/sub on variantChange. Works on the desktop
   main buy-button, the mobile sticky ATC bar (standard), and the mobile
   sticky ATC bar (minimalist) — all three share the `.product-form__submit`
   class, so one document-level delegated listener covers all.

   Runs only on product pages (template-product) to avoid false positives on
   card buttons elsewhere.
   ========================================================================== */
(function () {
  'use strict';

  /* Gate: product template only. Covers product.liquid + any variant.
     Hyper emits `product-template` on <body>; accept either naming. */
  var isProductPage =
    document.body.classList.contains('product-template') ||
    document.body.classList.contains('template-product') ||
    /^\/products\//.test(window.location.pathname);
  if (!isProductPage) return;

  var BIS_COPY = 'Notify me when available';
  var BIS_TRIGGER_ID = 'BIS_trigger';
  var PROXY_ATTR = 'data-bis-proxy';
  /* Cache the original ATC label per button so variant swaps restore it. */
  var ORIGINAL_TEXT_ATTR = 'data-bis-original-text';

  function getCurrentVariantId() {
    var input = document.querySelector('product-info [name="id"]');
    return input && input.value ? input.value : '';
  }

  function ensureTrigger() {
    var existing = document.getElementById(BIS_TRIGGER_ID);
    if (existing) return existing;
    var a = document.createElement('a');
    a.id = BIS_TRIGGER_ID;
    a.setAttribute('aria-hidden', 'true');
    a.setAttribute('tabindex', '-1');
    a.href = 'javascript:void(0)';
    a.style.display = 'none';
    a.setAttribute('data-variant-id', getCurrentVariantId());
    document.body.appendChild(a);
    return a;
  }

  function applyOOS(btn) {
    if (btn.getAttribute(PROXY_ATTR) === 'true') return;
    var textEl = btn.querySelector('.btn__text');
    if (textEl && !btn.hasAttribute(ORIGINAL_TEXT_ATTR)) {
      btn.setAttribute(ORIGINAL_TEXT_ATTR, textEl.textContent.trim());
    }
    btn.removeAttribute('disabled');
    btn.setAttribute(PROXY_ATTR, 'true');
    btn.classList.add('product-form__submit--oos');
    if (textEl) textEl.textContent = BIS_COPY;
  }

  function clearOOS(btn) {
    if (btn.getAttribute(PROXY_ATTR) !== 'true') return;
    btn.removeAttribute(PROXY_ATTR);
    btn.classList.remove('product-form__submit--oos');
    var original = btn.getAttribute(ORIGINAL_TEXT_ATTR);
    var textEl = btn.querySelector('.btn__text');
    if (textEl && original) textEl.textContent = original;
    /* Leave `disabled` handling to the theme's own variantChange subscribers;
       we never re-add it ourselves. */
  }

  /* Scan the page and flip each button based on the supplied availability.
     When `available` is undefined (initial load), use the button's `disabled`
     attribute as the signal. */
  function syncAllButtons(available) {
    var buttons = document.querySelectorAll('button.product-form__submit');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      /* Ignore buttons nested in quick-view (own lifecycle, own section). */
      if (btn.closest('quick-view-modal')) continue;
      var soldOut = available === undefined ? btn.hasAttribute('disabled') : !available;
      if (soldOut) {
        applyOOS(btn);
      } else {
        clearOOS(btn);
      }
    }
  }

  /* Capture-phase delegation — runs before the form's submit handler and before
     any sticky-atc-bar click handler, so we cleanly short-circuit submission. */
  document.addEventListener(
    'click',
    function (e) {
      if (!e.target || !e.target.closest) return;
      var btn = e.target.closest('button.product-form__submit[' + PROXY_ATTR + '="true"]');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var trigger = document.getElementById(BIS_TRIGGER_ID);
      if (trigger) trigger.click();
    },
    true,
  );

  function subscribeToVariantChange() {
    if (!window.FoxTheme || !window.FoxTheme.pubsub) return false;
    var events = window.FoxTheme.pubsub.PUB_SUB_EVENTS;
    if (!events || !events.variantChange) return false;
    window.FoxTheme.pubsub.subscribe(events.variantChange, function (event) {
      var variant = event && event.data && event.data.variant;
      var trigger = document.getElementById(BIS_TRIGGER_ID);
      if (trigger && variant) trigger.setAttribute('data-variant-id', String(variant.id));
      /* Defer to next tick so we win the race with the theme's own subscribers
         that may toggle `disabled` on the buttons during the same event. */
      setTimeout(function () {
        syncAllButtons(variant ? variant.available : undefined);
      }, 0);
    });
    return true;
  }

  function init() {
    ensureTrigger();
    syncAllButtons();
    /* FoxTheme loads synchronously in <head> before our defer'd custom.js runs,
       so pubsub is almost always ready. Retry once if not. */
    if (!subscribeToVariantChange()) {
      setTimeout(subscribeToVariantChange, 500);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================================================
   GA4 collection-page event tracking has MOVED to assets/custom-cx.js.

   It now detects the same interactions (view_item_list, select_item,
   filter_applied, sort_changed, load_more, no_results) and sends them straight
   to GA4 (Measurement Protocol / g-collect) from page context — superseding the
   retired custom web pixel. See analytics/README.md and
   plans/plan-collection-page-cro.md §4.
   ========================================================================== */
